<template>
  <div>
    <dx-modal
      :value="show"
      @input="close"
      title="Configurações de agendamentos"
      id="schedule-config-modal"
      size="lg">
      <template v-if="stage === 'schedules'">
        <div class="loading loading-lg" v-if="loading" />
        <template v-else>
          <div class="empty" v-if="schedules && schedules.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Agendas</p>
            <p class="empty-subtitle">
              Nenhuma agenda criada.
              <span v-if="canCreateSchedule">
                Para criar uma nova agenda, clique no botão abaixo.
              </span>
            </p>
            <button
              v-if="canCreateSchedule"
              class="btn btn-primary mt-2"
              @click="createSchedule"
            >Criar nova agenda</button>
          </div>
          <template v-else>
            <table class="table table-striped table-hover">
              <thead>
              <tr>
                <th style="width:40px">#</th>
                <th>Nome</th>
                <th>Local</th>
                <th>Tipo</th>
                <th/>
              </tr>
              </thead>
              <tbody>
              <tr
                class="c-hand"
                v-for="(item, i) in schedules"
                :key="i"
                @click="editSchedule(item)"
              >
                <td>{{ i + 1 }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.place ? item.place.name : '--' }}</td>
                <td>{{ getScheduleTypeName(item.type) }}</td>
                <td class="text-right" style="width: 80px">
                  <button
                    class="btn btn-sm btn-action btn-icon btn-secondary mr-1"
                    @click.stop="editSchedule(item)"
                  >
                    <fa-icon :icon="['fal', 'pencil']"/>
                  </button>
                  <button
                    v-if="canDeleteSchedule"
                    class="btn btn-sm btn-action btn-icon btn-error"
                    :class="{ loading: deleting && idx === i }"
                    :disabled="deleting"
                    @click.stop="removeSchedule(item, i)"
                  >
                    <fa-icon :icon="['fal', 'times']"/>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </template>
        </template>
      </template>

      <template v-if="stage === 'schedule-create'">
        <div class="loading loading-lg" v-if="hoursLoading" />
        <div v-show="!hoursLoading">

        <div class="columns form-group">
          <div
            class="column col-6 col-md-12 form-group"
            :class="{'has-error': $v.form.name.$error}"
          >
            <label for="name" class="form-label text-bold">Nome da agenda</label>
            <div class="input-group">
              <input
                type="text"
                id="name"
                name="name"
                class="form-input"
                placeholder="Dr José da Silva / Eletrocardiograma / Mapa 24 horas"
                autocomplete="nope"
                v-model="form.name"
                @change="$v.form.name.$touch()"
              >
            </div>
            <template v-if="$v.form.name.$error">
              <div class="form-input-hint"
                   v-if="!$v.form.name.required">Campo obrigatório</div>
            </template>
          </div>

          <div
            class="column col-3 col-md-6 col-sm-12 form-group"
            :class="{'has-error': $v.form.type.$error}"
          >
            <label for="name" class="form-label">Tipo</label>
            <select class="form-select" v-model="form.type">
              <option
                v-for="(text, value) in scheduleTypes"
                :value="value"
                :key="value"
              >{{ text }}</option>
            </select>
            <template v-if="$v.form.name.$error">
              <div
                class="form-input-hint"
                v-if="!$v.form.type.required"
              >Campo obrigatório</div>
            </template>
          </div>

          <div
            class="column col-3 col-md-6 col-sm-12 form-group"
            :class="{'has-error': $v.form.type.$error}"
          >
            <label for="name" class="form-label">Local</label>
            <select class="form-select" v-model="form.placeId">
              <option value="">[Selecione]</option>
              <option
                v-for="place in places"
                :value="place.id"
                :key="place.id"
              >{{ place.name }}</option>
            </select>
            <template v-if="$v.form.name.$error">
              <div
                class="form-input-hint"
                v-if="!$v.form.type.required"
              >Campo obrigatório</div>
            </template>
          </div>

          <div class="column col-12 form-group">
            <div class="communication-row">
              <span class="communication-label">Comunicações com o paciente</span>
              <label class="form-switch">
                <input type="checkbox" v-model="form.communication.whatsapp">
                <i class="form-icon"></i>
                <span>WhatsApp</span>
              </label>
              <label class="form-switch">
                <input type="checkbox" v-model="form.communication.email">
                <i class="form-icon"></i>
                <span>E-mail</span>
              </label>
            </div>
          </div>
        </div>

        <schedule-hours
          ref="hours"
          :schedule-id="form.id"
          @loading="hoursLoading = $event"
          @edit="openHourModal"
          @add="openHourModal()"
        />
        </div>
      </template>

      <template slot="footer">
        <button
          class="btn btn-icon btn-gray-outline mr-1"
          v-if="stage === 'schedule-create' && !hoursLoading"
          @click="backToList"
        >
          <fa-icon class="icon mr-2" :icon="['fal', 'chevron-left']" />Voltar
        </button>
        <button
          class="btn btn-primary btn-icon btn-icon-left mr-1"
          :class="{ loading: saving }"
          :disabled="saving"
          v-if="stage === 'schedule-create' && !hoursLoading"
          @click="save"
        ><fa-icon :icon="['fal', 'save']"/>Salvar</button>
        <button
          class="btn btn-primary btn-icon btn-icon-left mr-1"
          v-if="stage === 'schedules' && !loading && schedules && schedules.length > 0
            && canCreateSchedule"
          @click="createSchedule"
        ><fa-icon :icon="['fal', 'plus']"/>Criar nova agenda</button>
        <button class="btn" @click="close">Sair</button>
      </template>
    </dx-modal>

    <schedule-hour-config
      v-if="modalScheduleHourConfigShow"
      :show="modalScheduleHourConfigShow"
      :schedule-id="form.id"
      :hour-id="selectedHourId"
      @save="onSaveHour"
      @close="modalScheduleHourConfigShow = false"
    />
  </div>
</template>

<script>
import { types as scheduleTypes, getName as getScheduleTypeName } from '@/data/schedule-types';
import { required } from 'vuelidate/lib/validators';
import { resetCache } from '@/helpers/api';
import { CREATE_SCHEDULE, DELETE_SCHEDULE } from '@/data/actions/modules/clinical';
import ScheduleHours from '@/app/schedule/components/ScheduleHours.vue';
import ScheduleHourConfig from './ScheduleHourConfig.vue';

export default {
  components: {
    ScheduleHourConfig,
    ScheduleHours,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      schedules: [],
      places: [],
      scheduleTypes,
      getScheduleTypeName,
      loading: false,
      hoursLoading: false,
      saving: false,
      deleting: false,
      idx: null,
      selectedHourId: null,
      modalScheduleHourConfigShow: false,
      stage: 'schedules',
      form: this.blankForm(),
    };
  },
  async mounted() {
    this.loading = true;
    await Promise.all([this.loadPlaces(), this.loadSchedules()]);
    this.loading = false;
  },
  validations() {
    return {
      form: {
        name: {
          required,
        },
        type: {
          required,
        },
      },
    };
  },
  computed: {
    canCreateSchedule() {
      return this.$can(CREATE_SCHEDULE);
    },
    canDeleteSchedule() {
      return this.$can(DELETE_SCHEDULE);
    },
  },
  methods: {
    async loadPlaces() {
      const params = {
        type: 'clinical',
        limit: 0,
      };

      await this.$httpX
        .get('/places', { params })
        .then(({ data }) => {
          this.places = data.items;
        })
        .catch(() => {});
    },
    close() {
      this.$emit('close');
    },
    async loadSchedules() {
      await this.$httpX
        .get('/schedules', { params: { limit: 0 } })
        .then(({ data }) => {
          this.schedules = data.items;
        })
        .catch(() => {
          this.$toast.show('Erro ao carregar as agendas', { type: 'error' });
        });
    },
    createSchedule() {
      this.form = this.blankForm();
      this.$v.$reset();
      this.stage = 'schedule-create';
    },
    async editSchedule(item) {
      this.form = {
        ...this.blankForm(),
        id: item.id,
        name: item.name,
        type: item.type,
        placeId: item.place ? item.place.id : '',
        communication: item.communication || this.blankForm().communication,
      };
      this.$v.$reset();
      this.stage = 'schedule-create';
    },
    async backToList() {
      this.stage = 'schedules';
      this.loading = true;
      await this.loadSchedules();
      this.loading = false;
    },
    removeSchedule(item, i) {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir esta agenda e seus horários?</div>'
          + '</div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              this.idx = i;
              close();
              this.$httpX
                .delete(`/schedules/${item.id}`)
                .then(() => {
                  this.schedules.splice(i, 1);
                  resetCache();
                })
                .catch(() => {
                  this.$toast.show('Erro ao excluir a agenda', { type: 'error' });
                })
                .finally(() => {
                  this.deleting = false;
                  this.idx = null;
                });
            },
          },
        ],
      });
    },
    blankForm() {
      return {
        id: null,
        placeId: '',
        name: '',
        type: 'elective',
        communication: {
          whatsapp: false,
          email: false,
        },
      };
    },
    async openHourModal(item) {
      if (!this.form.id) {
        await this.save();
        if (!this.form.id) return;
      }
      this.selectedHourId = item ? item.id : null;
      this.modalScheduleHourConfigShow = true;
    },
    onSaveHour() {
      if (this.$refs.hours) this.$refs.hours.reload();
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      this.saving = true;

      const payload = {
        name: this.form.name,
        type: this.form.type,
        placeId: this.form.placeId || null,
        communication: this.form.communication,
      };

      const request = this.form.id
        ? this.$httpX.put(`/schedules/${this.form.id}`, payload)
        : this.$httpX.post('/schedules', payload);

      await request
        .then(({ data }) => {
          if (!this.form.id) {
            this.form.id = data.id;
          }
          this.$toast.show('Agenda salva com sucesso');
          resetCache();
        })
        .catch(() => {
          this.$toast.show('Erro ao salvar a agenda', { type: 'error' });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
#schedule-config-modal {
  .communication-row {
    align-items: center;
    background-color: lighten($info-color, 48%);
    border: $border-width solid lighten($info-color, 34%);
    border-radius: $border-radius;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
    .communication-label {
      color: $secondary-color-dark;
      font-size: $font-size-xs;
      font-weight: 700;
      letter-spacing: .04em;
      margin-right: $layout-spacing;
      text-transform: uppercase;
    }
    .form-switch {
      margin: 0;
    }
  }

}
</style>
